import {Injectable, inject} from '@angular/core'
import {ProductImageMapper} from '@models/product'
import {ActionService} from '@service/actions/action.service'
import {ConfigurationService} from '@service/configuration/configuration.service'
import {DistributorService} from '@service/distributors/distributor.service'
import {InfoService} from '@service/info/info.service'
import {OrderService} from '@service/orders/order/order.service'
import {TaxesService} from '@service/orders/taxes.service'
import {UMService} from '@service/orders/ums/um.service'
import {OutletService} from '@service/outlet/outlet.service'
import {PermissionsService} from '@service/permissions/permissions.service'
import {ProductService} from '@service/product.service'

/**
 * Orquestador de precargas para los flujos embebidos de pedido.
 *
 * @description
 * Cuando la app arranca embebida, no se ejecutan las rutas estándar de bootstrap. Este
 * servicio provee dos entradas específicas para precargar lo necesario antes de
 * mostrar la UI de creación o visualización de pedido:
 *
 * - `initOrderServices`: flujo completo de alta/edición de pedido.
 * - `initOrderViewServices`: flujo de solo lectura (detalle).
 *
 * Invoca `ConfigurationService.init()` primero para garantizar que el `ConfigAgent`
 * esté disponible y luego lanza el resto de precargas en paralelo.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * const initEmbedding = inject(EmbeddingInitService)
 * await initEmbedding.initOrderServices()
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class EmbeddingInitService {
  private readonly configService: ConfigurationService = inject(ConfigurationService)
  private readonly actionsService: ActionService = inject(ActionService)
  private readonly distributorsService: DistributorService = inject(DistributorService)
  private readonly umsService: UMService = inject(UMService)
  private readonly outletService: OutletService = inject(OutletService)
  private readonly productService: ProductService = inject(ProductService)
  private readonly permissionsService: PermissionsService = inject(PermissionsService)
  private readonly orderService: OrderService = inject(OrderService)
  private readonly infoService: InfoService = inject(InfoService)
  private readonly taxesService = inject(TaxesService)

  /**
   * Inicializa todos los servicios necesarios para crear o editar un pedido embebido.
   *
   * @description
   * Tras inicializar la configuración, precarga permisos, acciones, distribuidores, UMs,
   * códigos de relación del outlet, pedidos del outlet, clasificadores principales e
   * impuestos de forma concurrente.
   */
  initOrderServices = async (): Promise<void> => {
    await this.configService.init()
    await Promise.all([
      this.permissionsService.getPermissions(),
      this.actionsService.preloadActions(),
      this.distributorsService.preGetDistributors(),
      this.umsService.getUMs(),
      this.outletService.preGetOutletRelationCodes(),
      this.orderService.preloadOutletOrders(),
      this.infoService.getMainClassifiers(),
      this.taxesService.getData(),
    ])
  }

  /**
   * Inicializa los servicios necesarios para visualizar un pedido existente embebido.
   *
   * @description
   * Registra las imágenes recibidas en el `ProductService` (las del pedido actual) y luego
   * precarga la configuración junto con el resto de servicios de dominio necesarios en
   * modo solo lectura (no descarga acciones ni códigos de relación).
   *
   * @param productsImages Mapa `idcProducto → imagen` enviado por el host.
   */
  initOrderViewServices = async (productsImages: Map<number, ProductImageMapper>): Promise<void> => {
    productsImages.forEach((value, key) => {
      this.productService.addProductImageByIdcProducto(key, value)
    })
    await this.configService.init()
    await Promise.all([
      this.permissionsService.getPermissions(),
      this.distributorsService.preGetDistributors(),
      this.umsService.getUMs(),
      this.orderService.preloadOutletOrders(),
      this.infoService.getMainClassifiers(),
      this.taxesService.getData(),
    ])
  }
}
